import {
  type QueryClient,
  queryOptions,
  skipToken,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import { endpoints } from '@/config/apiEndpoints';
import { collections } from '@/config/databasePaths';
import { FIRESTORE_ENABLED } from '@/config/env';
import { useActiveOrganizationId } from '@/features/auth/useSession';
import { brandHubQueryKey, byName } from '@/features/brandHub/brandHub';
import {
  parseProductImport,
  parseProductImportCheck,
  type ProductImportCheck,
  type ProductImportRecord,
} from '@/features/brandHub/import/types';
import { type LiveCollection, useLiveCollection, useLiveMutation } from '@/hooks/useLiveData';
import { apiClient } from '@/lib/apiClient';
import { countDocuments } from '@/lib/firebase';
import type { PendingKind } from '@/lib/pendingWrites';
import { parseWriteResponse, readRecord, readRecords, requireParsed } from '@/lib/readers';
import { parseProduct, type Product } from './types';

const PRODUCT_LIMIT = 2000;

/** Returns the catalogue of one brand from its sub-collection, sorted by name. */
export function useBrandProducts(slug: string | null): LiveCollection<Product> {
  const organizationId = useActiveOrganizationId();

  const collection = useLiveCollection<Product>({
    path: collections.products(organizationId ?? '_', slug ?? '_'),
    httpPath: endpoints.brandHub.product.list(slug ?? '_'),
    queryKey: brandHubQueryKey('products', organizationId, slug),
    parse: parseProduct,
    limit: PRODUCT_LIMIT,
    enabled: Boolean(organizationId && slug),
  });

  return { ...collection, data: byName(collection.data, (product) => product.current.name) };
}

/** Returns the products whose name or sku contains the query. */
export function useFilteredProducts(products: Product[], query: string): Product[] {
  const needle = query.trim().toLowerCase();
  return products.filter(
    (product) => !needle || product.current.name.toLowerCase().includes(needle) || product.sku.toLowerCase().includes(needle),
  );
}

/** Returns the product count of one brand from one aggregate read, or from the HTTP list without Firestore. */
async function countBrandProducts(organizationId: string, slug: string): Promise<number> {
  if (!FIRESTORE_ENABLED) {
    return readRecords(readRecord(await apiClient.get(endpoints.brandHub.product.list(slug))).items).length;
  }
  return countDocuments(collections.products(organizationId, slug));
}

/** Returns the query of how many products one brand holds. */
export function productCountQuery(organizationId: string | null, slug: string | null) {
  return queryOptions({
    queryKey: brandHubQueryKey('product-count', organizationId, slug),
    queryFn: organizationId && slug ? () => countBrandProducts(organizationId, slug) : skipToken,
  });
}

/** Returns the product count of one brand, for a badge. It never reads the whole catalogue. */
export function useBrandProductCount(slug: string | null): number {
  const query = useQuery(productCountQuery(useActiveOrganizationId(), slug));
  return query.data ?? 0;
}

export interface ProductCreate {
  sku: string;
  name: string;
  description: string;
  dimension: string;
  price: string;
  category: string;
  market: string;
  productImageObjects: string[];
  moodImageObjects: string[];
}

/** An absent field is unchanged. The sku and the market are the id and cannot be updated. */
export type ProductUpdate = Partial<Omit<ProductCreate, 'sku' | 'market'>>;

function toUpdateBody(update: ProductUpdate): Record<string, unknown> {
  return {
    ...(update.name !== undefined && { name: update.name }),
    ...(update.description !== undefined && { description: update.description }),
    ...(update.dimension !== undefined && { dimension: update.dimension }),
    ...(update.price !== undefined && { price: update.price }),
    ...(update.category !== undefined && { category: update.category }),
    ...(update.productImageObjects !== undefined && {
      product_image_objects: update.productImageObjects,
    }),
    ...(update.moodImageObjects !== undefined && { mood_image_objects: update.moodImageObjects }),
  };
}

/** Moves the cached product count of one brand by `delta`, never below zero. */
function shiftProductCount(
  client: QueryClient,
  organizationId: string | null,
  slug: string,
  delta: number,
) {
  client.setQueryData(productCountQuery(organizationId, slug).queryKey, (count = 0) =>
    Math.max(0, count + delta),
  );
}

/** Sends one product request and puts the product in the response into the catalogue,
 *  marked until the stored data shows it. */
function useProductMutation<TVariables>(
  slug: string,
  kind: PendingKind,
  send: (variables: TVariables) => Promise<unknown>,
  countDelta = 0,
) {
  const client = useQueryClient();
  const organizationId = useActiveOrganizationId();
  return useLiveMutation({
    queryKey: brandHubQueryKey('products', organizationId, slug),
    kind,
    mutationFn: async (variables: TVariables) =>
      requireParsed(parseWriteResponse(await send(variables), 'product_id', parseProduct), 'product'),
    rowOf: (product: Product) => product,
    onSuccess: () => countDelta && shiftProductCount(client, organizationId, slug, countDelta),
  });
}

/** Adds one product to the brand's catalogue and to its count. */
export function useCreateProduct(slug: string) {
  return useProductMutation(
    slug,
    'create',
    (product: ProductCreate) =>
      apiClient.post(endpoints.brandHub.product.create(slug), {
        sku: product.sku,
        name: product.name,
        description: product.description,
        dimension: product.dimension,
        price: product.price,
        category: product.category,
        market: product.market,
        product_image_objects: product.productImageObjects,
        mood_image_objects: product.moodImageObjects,
      }),
    1,
  );
}

/** Changes one product of the brand. The response replaces its row in the list. */
export function useUpdateProduct(slug: string) {
  return useProductMutation(
    slug,
    'update',
    ({ productId, update }: { productId: string; update: ProductUpdate }) =>
      apiClient.put(
        endpoints.brandHub.product.update(slug, productId),
        toUpdateBody(update),
      ),
  );
}

/** Removes one product of the brand from the catalogue, the list and the count. */
export function useDeleteProduct(slug: string) {
  return useProductMutation(
    slug,
    'delete',
    (productId: string) =>
      apiClient.delete(endpoints.brandHub.product.delete(slug, productId)),
    -1,
  );
}

/** Returns what one uploaded file will do to the catalogue of the brand. It stores nothing. */
export function useCheckProductImport(slug: string) {
  return useMutation({
    mutationFn: async (fileObject: string): Promise<ProductImportCheck> => {
      const body = await apiClient.post(endpoints.brandHub.product.importCheck(slug), { file_object: fileObject });
      return parseProductImportCheck(readRecord(body));
    },
    meta: { toastsError: false },
  });
}

export interface ProductImportRequest {
  fileObject: string;
  fileName: string;
  /** The id of the import this one repeats, when it does. */
  rerunOf: string | null;
}

/** Imports one uploaded CSV into the catalogue of the brand and returns the completed record.
 *  It puts the record in the import history and adds the inserted count to the product count. */
export function useImportProducts(slug: string) {
  const client = useQueryClient();
  const organizationId = useActiveOrganizationId();
  return useLiveMutation({
    queryKey: brandHubQueryKey('product-imports', organizationId, slug),
    kind: 'create',
    mutationFn: async (request: ProductImportRequest): Promise<ProductImportRecord> => {
      const body = await apiClient.post(endpoints.brandHub.product.import(slug), {
        file_object: request.fileObject,
        file_name: request.fileName,
        ...(request.rerunOf && { rerun_of: request.rerunOf }),
      });
      return requireParsed(parseWriteResponse(body, 'import_id', parseProductImport), 'product import');
    },
    rowOf: (record: ProductImportRecord) => record,
    meta: { toastsError: false },
    onSuccess: (record) => shiftProductCount(client, organizationId, slug, record.insertedCount),
  });
}
